import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api, { ADMIN_BASE, assetUrl, formatApiError } from '../../lib/api';

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState('');

  const load = () => {
    setLoading(true);
    api.get('/products').then(r => setProducts(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const categories = useMemo(() => Array.from(new Set(products.map(p => p.category))).sort(), [products]);

  const filtered = useMemo(() => {
    return products.filter(p => {
      if (cat && p.category !== cat) return false;
      if (q) {
        const s = q.toLowerCase();
        if (!`${p.name_en} ${p.name_es} ${p.brand} ${p.model}`.toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [products, q, cat]);

  const onDelete = async (id) => {
    if (!window.confirm('Delete this product? This cannot be undone.')) return;
    setBusyId(id); setErr('');
    try {
      await api.delete(`/admin/products/${id}`);
      setProducts(ps => ps.filter(p => p.id !== id));
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  return (
    <div>
      <div className="admin-page-head">
        <div>
          <h1>Products</h1>
          <p className="muted">{products.length} total · {filtered.length} shown</p>
        </div>
        <Link to={`${ADMIN_BASE}/products/new`} className="btn btn-primary" data-testid="admin-new-product-btn">
          <i className="fa-solid fa-plus"></i>
          <span>New product</span>
        </Link>
      </div>

      <div className="admin-toolbar">
        <div className="admin-search">
          <i className="fa-solid fa-magnifying-glass"></i>
          <input type="search" placeholder="Search by name, brand, or model" value={q} onChange={e => setQ(e.target.value)} data-testid="admin-search-input" />
        </div>
        <select value={cat} onChange={e => setCat(e.target.value)} className="admin-select" data-testid="admin-cat-select">
          <option value="">All categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {err && <div className="form-error" style={{ marginBottom: 16 }}>{err}</div>}

      <div className="admin-table-wrap">
        <table className="admin-table" data-testid="admin-products-table">
          <thead>
            <tr>
              <th></th>
              <th>Name</th>
              <th>Brand</th>
              <th>Model</th>
              <th>Category</th>
              <th>Price</th>
              <th>Stock</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && Array.from({ length: 6 }).map((_, i) => (
              <tr key={`sk-${i}`}>
                <td colSpan={8}><div className="skeleton" style={{ height: 36, width: '100%' }} /></td>
              </tr>
            ))}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>No products</td></tr>
            )}
            {!loading && filtered.map(p => {
              const img = (p.images && p.images[0]) || p.image;
              return (
                <tr key={p.id} data-testid={`admin-row-${p.id}`}>
                  <td><div className="admin-thumb">{img && <img src={assetUrl(img)} alt={p.name_en} onError={(e) => { e.currentTarget.style.display = 'none'; }} />}</div></td>
                  <td><strong>{p.name_en}</strong></td>
                  <td>{p.brand}</td>
                  <td className="muted">{p.model}</td>
                  <td>{p.category}</td>
                  <td><strong>${p.price.toFixed(2)}</strong></td>
                  <td>
                    <span className={`stock-pill ${p.in_stock ? 'in' : 'out'}`}>
                      {p.in_stock ? 'In stock' : 'Out'}
                    </span>
                  </td>
                  <td className="admin-row-actions">
                    <Link to={`${ADMIN_BASE}/products/${p.id}/edit`} className="icon-btn" title="Edit" data-testid={`admin-edit-${p.id}`}>
                      <i className="fa-solid fa-pen"></i>
                    </Link>
                    <button onClick={() => onDelete(p.id)} className="icon-btn danger" title="Delete" disabled={busyId === p.id} data-testid={`admin-delete-${p.id}`}>
                      <i className={busyId === p.id ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-trash'}></i>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
